import { inject, Injectable } from '@angular/core';

import { ToolSearchModuleItemsLibrarySearchResultDto } from '@tfs/tool-library-search-api';

import { RumAnalyticsService } from './rum-analytics.service';

// Local type to avoid circular dependency with tool-library-shell
export type ItemWithManufacturer = {
  manufacturer?: string;
};

export type ToolItemInfo = {
  // actual time when request is finished in the browser
  requestDuration: number;
  // number of tools hit in the response
  toolsHit: number;
  totalToolCount: number;
  toolItemSearchUrl: string;
  manufacturers?: string[];
};

export type AssemblyInfo = {
  requestDuration: number;
  // number of tools hit in the response
  assemblyHit: number;
  totalAssemblyCount: number; // totalDocs in the response
  assemblySearchUrl: string;
};

@Injectable({
  providedIn: 'root',
})
export class DatadogUserInfoLoggerService {
  manufacturers = new Set<string>();

  toolData: ToolItemInfo = {
    requestDuration: 0,
    toolsHit: 0,
    totalToolCount: 0,
    toolItemSearchUrl: '',
    manufacturers: [],
  };

  assemblyInfo: AssemblyInfo = {
    requestDuration: 0,
    assemblyHit: 0,
    totalAssemblyCount: 0,
    assemblySearchUrl: '',
  };

  private readonly rum = inject(RumAnalyticsService);

  constructor() {
    this.observeToolItemSearch();
    this.observeAssemblySearch();
  }

  observeToolItemSearch() {
    const searchToolItemObserver = this.getToolItemsPerformanceDetails();
    searchToolItemObserver.observe({ entryTypes: ['resource'] });
  }

  observeAssemblySearch() {
    const searchAssemblyObserver = this.getAssemblyPerformanceDetails();
    searchAssemblyObserver.observe({ entryTypes: ['resource'] });
  }

  getAssemblyPerformanceDetails() {
    const observer = new PerformanceObserver(list => {
      list.getEntries().find(entry => {
        if (entry.name.includes('/search?context=Assemblies')) {
          this.assemblyInfo.requestDuration = entry.duration;
          this.assemblyInfo.assemblySearchUrl = entry.name;
          this.logAssemblySearchEndPointDetails();
          return true;
        }
        return false;
      });
    });

    return observer;
  }

  logAssemblySearchEndPointDetails() {
    this.rum.trackAction('search Assembly Endpoint', {
      requestDuration: this.assemblyInfo.requestDuration,
      assemblyHit: this.assemblyInfo.assemblyHit,
      totalAssemblyCount: this.assemblyInfo.totalAssemblyCount,
      assemblySearchUrl: this.assemblyInfo.assemblySearchUrl,
    });
  }

  setAssemblySearchData(assemblyData: ToolSearchModuleItemsLibrarySearchResultDto) {
    this.assemblyInfo.assemblyHit = assemblyData.hits?.length;
    this.assemblyInfo.totalAssemblyCount = assemblyData.hitsTotal ?? 0;
  }

  setToolItemSearchData(searchItemToolData: ItemWithManufacturer[], totalCount: number) {
    this.toolData.toolsHit = searchItemToolData.length;
    this.toolData.totalToolCount = totalCount;

    this.saveManufacturersName(searchItemToolData);

    this.toolData.manufacturers = Array.from(this.manufacturers);
  }

  saveManufacturersName(searchItemToolData: ItemWithManufacturer[]) {
    searchItemToolData.forEach(element => {
      if (element.manufacturer) {
        this.manufacturers.add(element.manufacturer);
      }
    });
  }

  logToolItemSearchEndPointDetails() {
    const manufacturers = this.toolData.manufacturers || [];

    this.rum.trackAction('search Tool Item Endpoint', {
      requestDuration: this.toolData.requestDuration,
      toolsHit: this.toolData.toolsHit,
      totalToolCount: this.toolData.totalToolCount,
      toolItemSearchUrl: this.toolData.toolItemSearchUrl,
      // Send manufacturers as an array - Datadog will display each name
      manufacturers: manufacturers,
      // Send as a readable string for easy viewing
      manufacturerNames: manufacturers.join(', '),
      manufacturerCount: manufacturers.length,
    });
  }

  getToolItemsPerformanceDetails(): PerformanceObserver {
    const observer = new PerformanceObserver(list => {
      list.getEntries().find(entry => {
        if (entry.name.includes('/search?context=Items')) {
          this.toolData.requestDuration = entry.duration;
          this.toolData.toolItemSearchUrl = entry.name;
          this.logToolItemSearchEndPointDetails();
          return true;
        }
        return false;
      });
    });

    return observer;
  }
}
